import { useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from 'services/context';
import { USER_ACTIONS, LANGAUGE_ACTIONS } from 'services/reducers/actionTypes';
import { EXTERNAL_PAGES, INPUT_TYPES, ROUTES } from 'constants/index.js';
import { useSessionStorage, useTranslation } from 'services/hooks';
import { isUserLanguageValid } from 'services/utils';
import Button from 'components/Button';
import Container from 'components/Container';
import Input from 'components/Input';
import Divider from 'components/Divider';
import OtpInputWithValidation from 'components/Input/OtpInputWithValidation';

import './style.css';
const Login = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const TRANSLATION_KEY = 'log_in';

	const { user, dispatchUser, Api, setLang } = useAppContext();
	const getTranslation = useTranslation();
	const [errorMessage, setErrorMessage] = useState(null);
	const [userName, setUserName] = useState(null);
	const [password, setPassword] = useState(null);
	const [requestOtp, setRequestOtp] = useState(false);
	const [otpValue, setOtpValue] = useState(null);

	const buttonRef = useRef(null);
	const [_, setCacheUser] = useSessionStorage('user', '');

	//handles onSubmit function for Login screen
	const _handleSubmit = async e => {
		e?.preventDefault();
		setErrorMessage(null);
		const form = new FormData(e.target);
		const formData = Object.fromEntries(form.entries());
		Object.entries(formData).forEach(([key, value]) => (formData[key] = formData[key].trim())); //remove spaces
		const loggedInUser = await Api.Login(formData);
		if (loggedInUser && !loggedInUser.unauthorized) {
			if (loggedInUser.needsOtp) {
				setRequestOtp(loggedInUser.needsOtp);
				return;
			}

			if (loggedInUser?.preferredLanguageId && isUserLanguageValid(loggedInUser?.preferredLanguageId))
				setLang({ type: LANGAUGE_ACTIONS.CHANGE_LANG, languageId: loggedInUser?.preferredLanguageId });

			const { isMgmtLogin } = loggedInUser;
			dispatchUser({ type: USER_ACTIONS.LOG_IN, payload: loggedInUser });
			setCacheUser(loggedInUser);

			if (isMgmtLogin) navigate(ROUTES.MANAGEMENT_PANEL);
			else navigate(ROUTES.PERSONAL_AREA);
		} else {
			const message = loggedInUser?.errorMessage || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_error_message`);
			setErrorMessage(message);
		}
	};

	//handles onSubmit function for verification screen
	const _handleVerificationSubmit = async e => {
		e?.preventDefault();
		setErrorMessage(null);

		if (!otpValue) {
			setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`));
			return;
		}
		const loggedInUser = await Api.ValidateOtp(userName, password, otpValue);
		if (!loggedInUser) {
			setErrorMessage(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_error_message`));
			return;
		}

		const { isMgmtLogin } = loggedInUser;
		dispatchUser({ type: USER_ACTIONS.LOG_IN, payload: loggedInUser });
		setCacheUser(loggedInUser);
		if (isMgmtLogin) navigate(ROUTES.MANAGEMENT_PANEL);
		else navigate(ROUTES.LOGIN);
	};

	//function that gets value from OtpInputWithValidation component and sets otp value on state
	const updateOtpValue = otpString => {
		if (!otpString) return;
		setOtpValue(otpString);
	};

	const renderLogin = useMemo(() => {
		if (requestOtp) {
			return (
				<>
					<div className="Verification_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_title`)}</div>
					<div className="Verification_sub_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_verification_sub_title`)}</div>
					<form onSubmit={_handleVerificationSubmit}>
						<div className="otp_input__wrapper">
							<OtpInputWithValidation numberOfDigits={4} onOtpChange={updateOtpValue} />
						</div>
						<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_button`)} />
					</form>

					{errorMessage && <div className="error_message">{errorMessage}</div>}
				</>
			);
		}
		return (
			<>
				<div className="main_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_title`)}</div>
				<form onSubmit={_handleSubmit}>
					<Input
						startIcon="emailIcon"
						placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_email_input_placeholder`)}
						type={INPUT_TYPES.Email.htmlType}
						name="userName"
						onChange={e => setUserName(e.target.value)}
					/>
					<Input
						startIcon="passwordIcon"
						placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_password_input_placeholder`)}
						type="Password"
						name="password"
						onChange={e => setPassword(e.target.value)}
					/>
					<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_button`)} ref={buttonRef} />
				</form>
				<div className="forgot_password">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_divider`)}
					<span onClick={() => navigate(`/${ROUTES.FORGOT_MY_PASSWORD}`)}>
						{' '}
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_forgot_password_button`)}
					</span>
				</div>
				<Divider dividerText={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_divider`)} />
				<div className="not_memeber">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_not_member_button`)}
					<a href={EXTERNAL_PAGES.REGISTER}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_click_here_button`)}</a>
				</div>
				{errorMessage && <div className="error_message">{errorMessage}</div>}
			</>
		);
	}, [requestOtp, otpValue, errorMessage, getTranslation]);

	//redirect if logged in
	if (user?.token && user?.isMgmtLogin) return <Navigate to={ROUTES.MANAGEMENT_PANEL} state={{ from: location }} replace />;
	else if (user?.token) return <Navigate to={ROUTES.PERSONAL_AREA} state={{ from: location }} replace />;

	return (
		<div className="Login">
			<Container>{renderLogin}</Container>
		</div>
	);
};

export default Login;
